'use client';

import React, { PropsWithChildren, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import {
    PropsWithAriaLabel,
    PropsWithClassName,
    PropsWithDataLayer,
    PropsWithHref,
    PropsWithId,
    PropsWithStructuredData,
    PropsWithStyle,
    PropsWithTestId,
} from '../../props';

export type GlowLinkProps = PropsWithStyle &
    PropsWithAriaLabel &
    PropsWithId &
    PropsWithTestId &
    PropsWithChildren &
    PropsWithClassName &
    PropsWithHref &
    PropsWithDataLayer &
    PropsWithStructuredData;

/**
 * A component that renders an anchor or span if the link is obfuscated.
 * @param style - Additional styles for the anchor or span.
 * @param ariaLabel - The aria-label for the anchor or span.
 * @param children - The content to render.
 * @param className - Additional classes to apply.
 * @param href - The href for the anchor.
 * @param target - The target for the anchor. Valid values are _blank, _self, _parent, _top (see Target enum in PropsWithCTA.ts).
 * @param titleReferent - The title for the anchor. Used when the label is not descriptive enough.
 * @param id - The id for the anchor or span.
 * @param dataTestId - The data-testid for the anchor or span.
 * @param isObfuscated - Whether the link should be obfuscated.
 * @param dataLayer - The data-layer for the anchor.
 * @param disabled - Whether the link should be disabled.
 * @param structuredData - Optional structured data for SEO. Can contain the following:
 * @param structuredData.itemScope - Indicates that defines the scope of associated metadata.
 * @param structuredData.itemProp - Used to add properties to an item.
 * @param structuredData.itemType - Specifies the URL of the vocabulary that will be used to define itemprop's (item properties) in the data structure.
 * @param structuredData.content - The content of the structured data.
 */
export const GlowLink: Component<GlowLinkProps> = ({
    style,
    ariaLabel,
    children,
    className,
    href,
    target,
    titleReferent,
    id,
    dataTestId,
    isObfuscated,
    dataLayer,
    disabled,
    structuredData,
}) => {
    const [obfuscatedHref, setObfuscatedHref] = useState<string>();
    useEffect(() => {
        if (isObfuscated) {
            setObfuscatedHref(window.btoa(encodeURIComponent(href)));
        }
    }, [href, isObfuscated]);

    return isObfuscated ? (
        <span
            role={'link'}
            data-obfuscated-href={obfuscatedHref}
            tabIndex={0}
            className={clsx(
                disabled ? 'pointer-events-none cursor-default' : 'cursor-pointer',
                className,
                dataLayer?.className,
            )}
            style={style}
            aria-label={ariaLabel}
            data-testid={dataTestId}
            data-layer-id={dataLayer?.id}
            data-layer-sub-id={dataLayer?.subId}
            data-layer-label={dataLayer?.label}
            data-layer-action={dataLayer?.action}
            itemProp={structuredData?.itemProp}
            itemScope={structuredData?.itemScope}
            itemType={structuredData?.itemType}
            content={structuredData?.content}
        >
            {children}
        </span>
    ) : (
        <a
            className={clsx(disabled && 'pointer-events-none cursor-default', className, dataLayer?.className)}
            href={href}
            target={target}
            aria-label={ariaLabel}
            title={titleReferent}
            id={id}
            style={style}
            data-testid={dataTestId}
            data-layer-id={dataLayer?.id}
            data-layer-sub-id={dataLayer?.subId}
            data-layer-label={dataLayer?.label}
            data-layer-action={dataLayer?.action}
            itemProp={structuredData?.itemProp}
            itemScope={structuredData?.itemScope}
            itemType={structuredData?.itemType}
            content={structuredData?.content}
        >
            {children}
        </a>
    );
};
